import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { campaignsApi, submissionsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function DataUpload() {
  const location = useLocation();
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(location.state?.campaignId || '');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    campaignsApi.list().then((r) => {
      const openC = r.data.filter(c => c.status === 'abierta');
      setCampaigns(openC);
      if (!selected && openC.length > 0) {
        // Prefer the most recent NON-future campaign (relative to today)
        const now = new Date();
        const currentKey = now.getFullYear() * 12 + now.getMonth() + 1;
        const past = openC.filter(c => (c.year * 12 + c.month) <= currentKey);
        setSelected((past[0] || openC[0]).id);
      }
    }).catch(() => toast.error('No se pudieron cargar campañas'));
  }, []); // eslint-disable-line

  const downloadTemplate = async () => {
    if (!selected) return toast.error('Seleccione una campaña');
    try {
      const r = await campaignsApi.downloadTemplate(selected);
      const camp = campaigns.find(c => c.id === selected);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `AEA_plantilla_${camp?.period_label?.replace(/ /g, '_') || 'datos'}.xlsx`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success('Plantilla descargada');
    } catch { toast.error('Error al descargar plantilla'); }
  };

  const submit = async () => {
    if (!selected) return toast.error('Seleccione una campaña');
    if (!file) return toast.error('Seleccione un archivo');
    setUploading(true);
    setResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await submissionsApi.submit(selected, fd);
      setResult(r.data);
      if (r.data.status === 'validada') toast.success(`Envío validado: ${r.data.valid_rows} indicadores`);
      else if (r.data.status === 'error') toast.error('Envío con errores');
      else toast.warning('Envío recibido con avisos');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al subir');
    } finally { setUploading(false); }
  };

  return (
    <div className="max-w-4xl space-y-6 animate-fade-in-up">
      <Card className="aea-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Upload className="w-5 h-5 text-aea-blue" /> Subir datos mensuales
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Descargue la plantilla Excel, rellene los valores y súbala. El sistema validará automáticamente formato, indicadores, unidades y duplicados.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Campaña</label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger data-testid="campaign-select"><SelectValue placeholder="Seleccione campaña abierta" /></SelectTrigger>
              <SelectContent>
                {campaigns.length === 0 && <SelectItem value="_none" disabled>No hay campañas abiertas</SelectItem>}
                {campaigns.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.period_label} · {c.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <FileSpreadsheet className="w-5 h-5 text-aea-blue mt-0.5" />
              <div>
                <div className="text-sm font-semibold">Plantilla oficial AEA</div>
                <div className="text-xs text-slate-500 mt-1">Contiene todos los indicadores con códigos, nombres y unidades. Rellena solo la columna "Valor".</div>
              </div>
            </div>
            <Button variant="outline" onClick={downloadTemplate} disabled={!selected} data-testid="btn-download-template">
              <Download className="w-4 h-4 mr-2" /> Descargar
            </Button>
          </div>

          <div>
            <label className="text-sm font-medium mb-1.5 block">Archivo (Excel .xlsx o CSV)</label>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setFile(e.target.files[0])}
              data-testid="file-input"
              className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-aea-blue/10 file:text-aea-blue hover:file:bg-aea-blue/20"
            />
            {file && <div className="text-xs text-slate-500 mt-1">📄 {file.name} ({(file.size / 1024).toFixed(1)} KB)</div>}
          </div>

          <Button className="bg-aea-blue hover:bg-aea-blue-dk" onClick={submit} disabled={uploading || !file || !selected} data-testid="btn-submit-data">
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Procesando...</> : <><Upload className="w-4 h-4 mr-2" /> Enviar datos</>}
          </Button>
        </CardContent>
      </Card>

      {result && (
        <Card className="aea-card animate-fade-in-up">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              {result.status === 'validada' ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                : result.status === 'error' ? <XCircle className="w-5 h-5 text-red-600" />
                : <AlertCircle className="w-5 h-5 text-amber-600" />}
              Resultado del envío
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="border rounded-lg p-3"><div className="text-xs text-slate-500">Filas totales</div><div className="text-2xl font-outfit font-semibold">{result.total_rows}</div></div>
              <div className="border rounded-lg p-3"><div className="text-xs text-slate-500">Válidas</div><div className="text-2xl font-outfit font-semibold text-emerald-600">{result.valid_rows}</div></div>
              <div className="border rounded-lg p-3"><div className="text-xs text-slate-500">Con errores</div><div className="text-2xl font-outfit font-semibold text-red-600">{result.error_rows}</div></div>
            </div>
            {result.errors?.length > 0 && (
              <div>
                <div className="text-sm font-semibold text-slate-700 mb-2">Errores detectados</div>
                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr><th className="p-2 text-left">Fila</th><th className="p-2 text-left">Indicador</th><th className="p-2 text-left">Motivo</th></tr>
                    </thead>
                    <tbody>
                      {result.errors.map((e, i) => (
                        <tr key={i} className="border-t"><td className="p-2 font-mono">{e.row}</td><td className="p-2 font-mono">{e.code}</td><td className="p-2 text-red-700">{e.error}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
